import type { JournalEntry, JournalInsights, GroupedInsights } from '../types/journal';

/**
 * Computes the arithmetic mean of cannabinoidMg values, rounded to 1 decimal place.
 */
export function calculateAverageDose(entries: JournalEntry[]): number {
  if (entries.length === 0) return 0;
  const sum = entries.reduce((acc, e) => acc + e.cannabinoidMg, 0);
  return Math.round((sum / entries.length) * 10) / 10;
}

/**
 * Computes the arithmetic mean of each timing field (onset, peak, duration)
 * from only non-null values, rounded to the nearest whole minute.
 * Returns null for a timing field if fewer than 2 entries have that field non-null.
 */
export function calculateTimingAverages(entries: JournalEntry[]): {
  onset: number | null;
  peak: number | null;
  duration: number | null;
} {
  const onsetValues = entries
    .map((e) => e.onsetMinutes)
    .filter((v): v is number => v != null);
  const peakValues = entries
    .map((e) => e.peakMinutes)
    .filter((v): v is number => v != null);
  const durationValues = entries
    .map((e) => e.durationMinutes)
    .filter((v): v is number => v != null);

  const avg = (values: number[]): number | null => {
    if (values.length < 2) return null;
    const sum = values.reduce((acc, v) => acc + v, 0);
    return Math.round(sum / values.length);
  };

  return {
    onset: avg(onsetValues),
    peak: avg(peakValues),
    duration: avg(durationValues),
  };
}

/**
 * Counts frequency of each effect across all entries, returns up to `limit`
 * (default 5) in descending frequency order.
 */
export function calculateTopEffects(
  entries: JournalEntry[],
  limit: number = 5
): { effect: string; count: number }[] {
  const counts = new Map<string, number>();

  for (const entry of entries) {
    if (entry.effects) {
      for (const effect of entry.effects) {
        counts.set(effect, (counts.get(effect) ?? 0) + 1);
      }
    }
  }

  return Array.from(counts.entries())
    .map(([effect, count]) => ({ effect, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

/**
 * Computes total entries / (daySpan / 7), rounded to 1 decimal.
 * Returns null if entries span fewer than 7 days.
 * Day span = days between earliest and most recent consumedAt.
 */
export function calculateConsumptionFrequency(
  entries: JournalEntry[]
): number | null {
  if (entries.length < 2) return null;

  const timestamps = entries.map((e) => new Date(e.consumedAt).getTime());
  const earliest = Math.min(...timestamps);
  const latest = Math.max(...timestamps);

  const daySpan = (latest - earliest) / (1000 * 60 * 60 * 24);

  if (daySpan < 7) return null;

  const weeks = daySpan / 7;
  return Math.round((entries.length / weeks) * 10) / 10;
}

/**
 * Main entry point for insights calculation.
 * Groups entries by cannabinoidType, computes all sub-metrics for each group
 * with 3+ entries. Returns GroupedInsights[] — one item per type if multiple
 * types exist, or a single ungrouped result if only one type.
 */
export function calculateInsights(entries: JournalEntry[]): GroupedInsights[] {
  if (entries.length === 0) return [];

  // Group entries by cannabinoidType
  const groups = new Map<string, JournalEntry[]>();
  for (const entry of entries) {
    const type = entry.cannabinoidType;
    if (!groups.has(type)) {
      groups.set(type, []);
    }
    groups.get(type)!.push(entry);
  }

  const results: GroupedInsights[] = [];

  for (const [cannabinoidType, groupEntries] of groups) {
    // Only compute insights for groups with 3+ entries
    if (groupEntries.length < 3) continue;

    const timingAverages = calculateTimingAverages(groupEntries);

    const insights: JournalInsights = {
      averageDoseMg: calculateAverageDose(groupEntries),
      averageOnsetMinutes: timingAverages.onset,
      averagePeakMinutes: timingAverages.peak,
      averageDurationMinutes: timingAverages.duration,
      topEffects: calculateTopEffects(groupEntries),
      consumptionFrequencyPerWeek: calculateConsumptionFrequency(groupEntries),
    };

    results.push({
      cannabinoidType,
      insights,
      entryCount: groupEntries.length,
    });
  }

  return results;
}
